import { useTranslation } from 'react-i18next';
import { ChevronDown, Layers, ListChecks, Pencil, Triangle, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BoardDashboardClimb } from '../../../types/board-dashboard';
import { ClimbStatChips } from './stat-chips';

interface BoardClimbRowProps {
  climb: BoardDashboardClimb;
  /**
   * The problem currently drawn on the photo. Carried as an id rather than a
   * boolean because an expanded variant can be the selected one, and it has to
   * mark itself.
   */
  selectedId: string | null;
  expanded: boolean;
  /** True when this board grades per angle, so the angle is worth showing. */
  showInclination: boolean;
  /**
   * Whether these problems are this gym's to correct. False on a global board,
   * whose problems come from a library every gym with the wall climbs.
   */
  canEdit: boolean;
  onSelect: (climb: BoardDashboardClimb) => void;
  onEdit: (climb: BoardDashboardClimb) => void;
  onOpenHistory: (climb: BoardDashboardClimb) => void;
  onToggleVariants: (climbId: string) => void;
}

/** Icon-and-number labels, gathered once so both the row and its variants share them. */
function useStatLabels() {
  const { t } = useTranslation();

  return {
    sends: t('gymAdmin.boardDashboard.stats.sends'),
    attempts: t('gymAdmin.boardDashboard.stats.attempts'),
    holds: t('gymAdmin.boardDashboard.stats.holds'),
    rating: t('gymAdmin.boardDashboard.stats.rating'),
    lightings: t('gymAdmin.boardDashboard.stats.lightings'),
    shares: t('gymAdmin.boardDashboard.stats.shares'),
    circuit: t('gymAdmin.boardDashboard.stats.circuit'),
    forcedHands: t('gymAdmin.boardDashboard.stats.forcedHands'),
  };
}

/**
 * What the selected problem shows on top of its numbers: how it is described,
 * who set it, and the two things done with it from here — reading its history
 * and correcting it. Kept to the selected row so the list stays a column of
 * numbers to scan.
 */
function SelectedDetails({
  climb,
  canEdit,
  onEdit,
  onOpenHistory,
}: {
  climb: BoardDashboardClimb;
  canEdit: boolean;
  onEdit: (climb: BoardDashboardClimb) => void;
  onOpenHistory: (climb: BoardDashboardClimb) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 pt-1" data-testid="board-climb-details">
      {climb.description && (
        <p className="whitespace-pre-line text-sm text-muted-foreground">{climb.description}</p>
      )}
      {climb.route_setter_name && (
        <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="size-3.5" aria-hidden />
          {t('gymAdmin.boardDashboard.setBy', { name: climb.route_setter_name })}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onOpenHistory(climb)}>
          <ListChecks className="mr-1.5 size-3.5" aria-hidden />
          {t('gymAdmin.boardDashboard.openHistory')}
        </Button>
        {canEdit && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onEdit(climb)}>
            <Pencil className="mr-1.5 size-3.5" aria-hidden />
            {t('gymAdmin.boardDashboard.editProblem')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * One problem in the dashboard list. Selecting it draws its holds on the board
 * photo; the variants counter opens the same problem's other angles inline,
 * each of which can be selected in turn. The selected one opens up in place,
 * edged in the primary colour, with the rest of what there is to know about it.
 */
export function BoardClimbRow({
  climb,
  selectedId,
  expanded,
  showInclination,
  canEdit,
  onSelect,
  onEdit,
  onOpenHistory,
  onToggleVariants,
}: BoardClimbRowProps) {
  const { t, i18n } = useTranslation();
  const labels = useStatLabels();

  const selected = selectedId === climb.id;
  const variants = climb.variants ?? [];
  const setOn = climb.created_at
    ? new Date(climb.created_at).toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  return (
    // `data-climb-id` is how the page scrolls a keyboard-picked row back into
    // view without every row needing a ref of its own.
    <div
      data-climb-id={climb.id}
      // Every row carries the edge, transparent until it is the selected one,
      // so picking a problem does not shift the list sideways.
      className={cn(
        'border-b border-l-4 border-l-transparent last:border-b-0',
        selected && 'border-l-primary bg-primary/5'
      )}
    >
      <div>
        <button
          type="button"
          onClick={() => onSelect(climb)}
          aria-pressed={selected}
          className="flex w-full min-w-0 flex-col gap-1.5 px-3 py-2.5 text-left transition-colors hover:bg-accent/50"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{climb.name}</span>
            {climb.grade && (
              <Badge variant="secondary" className="shrink-0 tabular-nums">
                {climb.grade.name}
              </Badge>
            )}
            {showInclination && climb.inclination !== null && (
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <Triangle className="size-3" aria-hidden />
                {t('gymAdmin.boardDashboard.degrees', { value: climb.inclination })}
              </span>
            )}
            {setOn && (
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">{setOn}</span>
            )}
          </div>

          <ClimbStatChips climb={climb} labels={labels} />
        </button>

        {/* Outside the row's own button: the buttons in it are things of their
            own to click on the same problem. */}
        {selected && (
          <div className="px-3 pb-2.5">
            <SelectedDetails
              climb={climb}
              canEdit={canEdit}
              onEdit={onEdit}
              onOpenHistory={onOpenHistory}
            />
          </div>
        )}
      </div>

      {variants.length > 0 && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => onToggleVariants(climb.id)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent"
          >
            <Layers className="size-3.5" aria-hidden />
            {t('gymAdmin.boardDashboard.otherAngles', { count: variants.length })}
            <ChevronDown
              className={cn('size-3.5 transition-transform', expanded && 'rotate-180')}
              aria-hidden
            />
          </button>

          {expanded && (
            <div className="mt-2 space-y-1 border-l-2 border-muted pl-3">
              {variants.map((variant) => (
                // An angle is a problem of its own — same grade ladder, same
                // numbers, its own correction — so it opens up the same way.
                <div
                  key={variant.id}
                  data-climb-id={variant.id}
                  className={cn(
                    'rounded-md border-l-4 border-l-transparent',
                    selectedId === variant.id && 'border-l-primary bg-primary/10'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(variant)}
                    aria-pressed={selectedId === variant.id}
                    className="flex w-full min-w-0 flex-col gap-1 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
                  >
                    <span className="flex items-center gap-2 text-xs">
                      <Triangle className="size-3 text-muted-foreground" aria-hidden />
                      <span className="font-medium">
                        {variant.inclination === null
                          ? t('gymAdmin.boardDashboard.noAngle')
                          : t('gymAdmin.boardDashboard.degrees', { value: variant.inclination })}
                      </span>
                      {variant.grade && (
                        <Badge variant="outline" className="tabular-nums">
                          {variant.grade.name}
                        </Badge>
                      )}
                    </span>
                    <ClimbStatChips climb={variant} labels={labels} />
                  </button>

                  {selectedId === variant.id && (
                    <div className="px-2 pb-2">
                      <SelectedDetails
                        climb={variant}
                        canEdit={canEdit}
                        onEdit={onEdit}
                        onOpenHistory={onOpenHistory}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
